/**
 * Click and UTM attribution.
 *
 * On every page load / route change, captureAttribution() reads the URL:
 *   - utm_source, utm_medium, utm_campaign, utm_term, utm_content, ad_id,
 *     fbclid and the landing URL.
 *   - first_touch: the first visit with UTMs or an fbclid. Never overwritten.
 *   - last_touch: the latest such visit (WhatsApp and email links count too).
 *   - A visit with neither changes nothing.
 * Stored in localStorage as gn_attribution.
 *
 * fbclid also refreshes the _fbc cookie (fb.1.{ms}.{fbclid}, 90 days, on
 * .gradnext.co) whenever it is missing or holds a different click id, because
 * Meta expects the latest click.
 *
 * attributionHeader() is sent as X-GN-Attribution on non-GET API calls (see
 * index.js); the backend whitelists and size-caps it, and ignores it if bad.
 */

const STORAGE_KEY = 'gn_attribution';
const TOUCH_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ad_id', 'fbclid'];
const FBC_MAX_AGE = 90 * 24 * 60 * 60;

const readCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (e) {
    return null;
  }
};

const cookieDomain = () => {
  const host = window.location.hostname;
  return host === 'gradnext.co' || host.endsWith('.gradnext.co') ? '.gradnext.co' : null;
};

const setFbc = (fbclid) => {
  const current = readCookie('_fbc');
  // _fbc is fb.1.{creation ms}.{fbclid}; keep it if it already holds this click.
  if (current && current.split('.').slice(3).join('.') === fbclid) return;
  const domain = cookieDomain();
  try {
    document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; max-age=${FBC_MAX_AGE}; path=/; SameSite=Lax`
      + (domain ? `; domain=${domain}` : '')
      + (window.location.protocol === 'https:' ? '; Secure' : '');
  } catch (e) {
    // cookies blocked; nothing to do
  }
};

export const readAttribution = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

export const captureAttribution = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const touch = {};
    TOUCH_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) touch[key] = value.slice(0, 500);
    });
    if (touch.fbclid) setFbc(touch.fbclid);
    if (Object.keys(touch).length === 0) return;

    touch.landing_url = window.location.href.slice(0, 500);
    touch.at = new Date().toISOString();
    const stored = readAttribution();
    const next = { ...stored, last_touch: touch };
    if (!stored.first_touch) next.first_touch = touch;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    // storage blocked or URL unreadable: attribution is best-effort
  }
};

// UTF-8 safe base64 (UTM values can carry non-ASCII text).
const toBase64 = (text) => window.btoa(
  encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))),
);

/** Value for the X-GN-Attribution header, or null when there is nothing to send. */
export const attributionHeader = () => {
  try {
    const { first_touch: firstTouch, last_touch: lastTouch } = readAttribution();
    const payload = {
      fbp: readCookie('_fbp') || undefined,
      fbc: readCookie('_fbc') || undefined,
      page_url: window.location.href.slice(0, 500),
    };
    if (firstTouch) payload.first_touch = firstTouch;
    if (lastTouch) payload.last_touch = lastTouch;
    const encoded = toBase64(JSON.stringify(payload));
    return encoded.length <= 4096 ? encoded : null;
  } catch (e) {
    return null;
  }
};
